// The browser protocol between the dashboard (parent window) and an app iframe.
// Mirrors the fork's Frame.vue and helper/w3sendAppHost.js; the dev host implements the parent
// side with these same helpers so the two can never drift.

import type { Role } from './types.ts';

/** Posted by the iframe (as a plain string) to ask for the app context. */
export const FETCH_INFO_MESSAGE = 'chatwoot-dashboard-app:fetch-info';

/** Events the dashboard sends to the iframe, as `JSON.stringify({ event, data })`. */
export const APP_CONTEXT_EVENT = 'appContext';
export const APP_TOKEN_EVENT = 'appToken';
/** Sent to an app's sidebar frame to show one of its own pages (after an `open-app` action). */
export const APP_ROUTE_EVENT = 'w3send-app:route';

/** Messages the iframe sends to the dashboard (objects). */
export const HOST_ACTIONS = {
  REFRESH_TOKEN: 'w3send-app:refresh-token',
  NAVIGATE: 'w3send-app:navigate',
  TOAST: 'w3send-app:toast',
  OPEN_URL: 'w3send-app:open-url',
  /** Opens this app's own sidebar page at `path` (e.g. from its conversation tab). */
  OPEN_APP: 'w3send-app:open-app',
  /** Conversation tab only: puts `text` into the reply box for the agent to review and send. */
  INSERT_REPLY: 'w3send-app:insert-reply',
} as const;

export const APP_PATH_MAX_LENGTH = 500;

/** A path inside the app, like the fork's `isAppPath`: one leading slash, URL characters only. */
export const isAppPath = (path: unknown): path is string =>
  typeof path === 'string' &&
  path.length <= APP_PATH_MAX_LENGTH &&
  /^\/(?!\/)[A-Za-z0-9\-._~!$&'()*+,;=:@%/?#]*$/.test(path);

export const TOAST_MAX_LENGTH = 300;
export const INSERT_REPLY_MAX_LENGTH = 4096;

export type Theme = 'light' | 'dark';

export interface HostAgent {
  id: number;
  name: string;
  email: string;
  role: Role;
}

export interface HostAccount {
  id: number;
  name: string;
}

export interface HostConversation {
  /** Display id. */
  id: number;
  inbox_id: number;
}

export interface HostContact {
  id: number;
}

/** `data` of the `appContext` event. `conversation`/`contact` only in conversation tabs. */
export interface AppContextPayload {
  appToken: string;
  account: HostAccount;
  currentAgent: HostAgent;
  theme: Theme;
  conversation?: HostConversation;
  contact?: HostContact;
}

export type NavigateTarget = 'conversation' | 'contact';

export type HostAction =
  | { type: typeof HOST_ACTIONS.REFRESH_TOKEN }
  | { type: typeof HOST_ACTIONS.NAVIGATE; target: NavigateTarget; id: number }
  | { type: typeof HOST_ACTIONS.TOAST; message: string }
  | { type: typeof HOST_ACTIONS.OPEN_URL; url: string }
  | { type: typeof HOST_ACTIONS.OPEN_APP; path: string }
  | { type: typeof HOST_ACTIONS.INSERT_REPLY; text: string };

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const positiveInt = (value: unknown): number | null => {
  const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof number === 'number' && Number.isInteger(number) && number > 0 ? number : null;
};

/**
 * Validates a host action posted by an iframe. Same rules as the fork's `parseHostAction`:
 * unknown types, bad navigate targets/ids, empty toasts, non-https URLs and empty or over-long
 * reply texts are rejected.
 */
export function parseHostAction(data: unknown): HostAction | null {
  if (!isObject(data)) return null;
  switch (data.type) {
    case HOST_ACTIONS.REFRESH_TOKEN:
      return { type: HOST_ACTIONS.REFRESH_TOKEN };
    case HOST_ACTIONS.NAVIGATE: {
      const id = positiveInt(data.id);
      if (data.target !== 'conversation' && data.target !== 'contact') return null;
      return id ? { type: HOST_ACTIONS.NAVIGATE, target: data.target, id } : null;
    }
    case HOST_ACTIONS.TOAST: {
      const message = typeof data.message === 'string' ? data.message.slice(0, TOAST_MAX_LENGTH) : '';
      return message ? { type: HOST_ACTIONS.TOAST, message } : null;
    }
    case HOST_ACTIONS.OPEN_APP:
      return isAppPath(data.path) ? { type: HOST_ACTIONS.OPEN_APP, path: data.path } : null;
    case HOST_ACTIONS.INSERT_REPLY: {
      const text = typeof data.text === 'string' ? data.text.trim() : '';
      return text && text.length <= INSERT_REPLY_MAX_LENGTH
        ? { type: HOST_ACTIONS.INSERT_REPLY, text }
        : null;
    }
    case HOST_ACTIONS.OPEN_URL:
      return typeof data.url === 'string' && /^https:\/\//.test(data.url)
        ? { type: HOST_ACTIONS.OPEN_URL, url: data.url }
        : null;
    default:
      return null;
  }
}

export type DashboardMessage =
  | { event: typeof APP_CONTEXT_EVENT; data: AppContextPayload }
  | { event: typeof APP_TOKEN_EVENT; data: { appToken: string | null } }
  | { event: typeof APP_ROUTE_EVENT; data: { path: string } };

const parseAgent = (value: unknown): HostAgent | null => {
  if (!isObject(value)) return null;
  const id = positiveInt(value.id);
  if (!id) return null;
  return {
    id,
    name: typeof value.name === 'string' ? value.name : '',
    email: typeof value.email === 'string' ? value.email : '',
    role: value.role === 'administrator' ? 'administrator' : 'agent',
  };
};

/**
 * Parses a message from the dashboard. The dashboard always posts JSON strings; anything else
 * (objects, other apps' chatter, malformed JSON) is ignored. Returns null for unknown messages.
 *
 * An `appContext` whose `appToken` is missing (the dashboard could not mint one) is returned with
 * an empty token so the caller can surface an error instead of waiting forever.
 */
export function parseDashboardMessage(raw: unknown): DashboardMessage | null {
  if (typeof raw !== 'string') return null;
  let message: unknown;
  try {
    message = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isObject(message) || !isObject(message.data)) return null;
  const { data } = message;

  if (message.event === APP_TOKEN_EVENT) {
    return {
      event: APP_TOKEN_EVENT,
      data: { appToken: typeof data.appToken === 'string' && data.appToken ? data.appToken : null },
    };
  }
  if (message.event === APP_ROUTE_EVENT) {
    return isAppPath(data.path) ? { event: APP_ROUTE_EVENT, data: { path: data.path } } : null;
  }
  if (message.event !== APP_CONTEXT_EVENT) return null;

  const accountId = isObject(data.account) ? positiveInt(data.account.id) : null;
  const agent = parseAgent(data.currentAgent);
  if (!accountId || !agent) return null;

  const payload: AppContextPayload = {
    appToken: typeof data.appToken === 'string' ? data.appToken : '',
    account: {
      id: accountId,
      name: isObject(data.account) && typeof data.account.name === 'string' ? data.account.name : '',
    },
    currentAgent: agent,
    theme: data.theme === 'dark' ? 'dark' : 'light',
  };
  if (isObject(data.conversation)) {
    const id = positiveInt(data.conversation.id);
    const inboxId = positiveInt(data.conversation.inbox_id);
    if (id && inboxId) payload.conversation = { id, inbox_id: inboxId };
  }
  if (isObject(data.contact)) {
    const id = positiveInt(data.contact.id);
    if (id) payload.contact = { id };
  }
  return { event: APP_CONTEXT_EVENT, data: payload };
}

/** `scheme://host[:port]` of a URL, or null (mirrors the fork's `frameOrigin`). */
export function originOf(url: string): string | null {
  try {
    const { origin } = new URL(url);
    return origin && origin !== 'null' ? origin : null;
  } catch {
    return null;
  }
}
